import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Send, Save } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { CONDITION_META } from "../../lib/format";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { Spinner } from "../dashboard/DashboardShell";
import ReportPhotoUploader from "./ReportPhotoUploader";

const CONDS = ["baik", "perlu_perhatian", "rusak", "tidak_dicek"];
const RECS = [["layak", "Layak Dibeli"], ["layak_dengan_catatan", "Layak dengan Catatan"], ["tidak_layak", "Tidak Direkomendasikan"]];

export default function ChecklistForm({ order, report = null, onSubmitted }) {
  const editing = !!report;
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["checklist", order.vehicle_type], queryFn: () => api.get(`/checklists/${order.vehicle_type}`).then((r) => r.data) });
  const [items, setItems] = useState(() => editing ? Object.fromEntries((report.items || []).map((it) => [it.key, { condition: it.condition, note: it.note || "" }])) : {});
  const [summary, setSummary] = useState(report?.summary || "");
  const [rec, setRec] = useState(report?.recommendation || "layak");
  const [cost, setCost] = useState(report?.estimated_repair_cost ? String(report.estimated_repair_cost) : "");
  const [photos, setPhotos] = useState(report?.photos || []);
  const [busy, setBusy] = useState(false);

  const set = (key, patch) => setItems((s) => ({ ...s, [key]: { condition: "baik", note: "", ...s[key], ...patch } }));
  const total = data?.groups.reduce((n, g) => n + g.items.length, 0) || 0;
  const filled = Object.keys(items).length;

  const submit = async () => {
    if (filled < total) return toast.error(`Lengkapi semua item checklist (${filled}/${total}).`);
    if (summary.trim().length < 10) return toast.error("Tulis ringkasan minimal 10 karakter.");
    setBusy(true);
    try {
      const payload = {
        items: Object.entries(items).map(([key, v]) => ({ key, condition: v.condition, note: v.note })),
        summary, recommendation: rec, estimated_repair_cost: cost ? Number(cost) : null, photos,
      };
      if (editing) await api.put(`/orders/${order.order_id}/report`, payload);
      else await api.post(`/orders/${order.order_id}/report`, payload);
      toast.success(editing ? "Laporan berhasil diperbarui." : "Laporan berhasil dikirim ke pengguna.");
      qc.invalidateQueries({ queryKey: ["order", order.order_id] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["reports"] });
      qc.invalidateQueries({ queryKey: ["inspector-dashboard"] });
      onSubmitted?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (isLoading) return <Spinner />;
  return (
    <div data-testid="checklist-form">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs font-semibold text-slate-500">Progress checklist</p>
        <span className="text-xs font-bold text-blue-600" data-testid="checklist-progress">{filled}/{total}</span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-5"><div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${total ? (filled / total) * 100 : 0}%` }} /></div>
      <div className="space-y-5">
        {data.groups.map((g) => (
          <div key={g.key}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-extrabold text-slate-800 uppercase tracking-wide">{g.label}</p>
              <button onClick={() => g.items.forEach((it) => set(it.key, { condition: "baik" }))} className="text-[11px] font-bold text-blue-600">Semua baik</button>
            </div>
            <div className="space-y-2">
              {g.items.map((it) => {
                const v = items[it.key];
                return (
                  <div key={it.key} className="border border-slate-100 rounded-xl p-3" data-testid={`checklist-item-${it.key}`}>
                    <p className="text-[13px] font-semibold text-slate-700">{it.label}</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {CONDS.map((c) => (
                        <button key={c} onClick={() => set(it.key, { condition: c })} data-testid={`cond-${it.key}-${c}`}
                          className={`text-[11px] font-bold rounded-full px-2.5 py-1 border transition-colors ${v?.condition === c ? CONDITION_META[c].cls + " ring-2 ring-offset-1 ring-blue-200" : "bg-white text-slate-400 border-slate-200 hover:border-slate-300"}`}>
                          {CONDITION_META[c].label}
                        </button>
                      ))}
                    </div>
                    {v && v.condition !== "baik" && (
                      <Input value={v.note} onChange={(e) => set(it.key, { note: e.target.value })} placeholder="Catatan temuan..." className="mt-2 h-9 text-xs" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 space-y-4 border-t border-slate-100 pt-5">
        <ReportPhotoUploader photos={photos} onChange={setPhotos} />
        <div>
          <label className="text-xs font-bold text-slate-600">Ringkasan hasil inspeksi</label>
          <Textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={4} data-testid="report-summary-input" placeholder="Jelaskan kondisi kendaraan secara keseluruhan, temuan penting, dan saran untuk pembeli..." className="mt-1.5 text-sm" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold text-slate-600">Rekomendasi</label>
            <div className="flex flex-col gap-1.5 mt-1.5">
              {RECS.map(([id, lbl]) => (
                <button key={id} onClick={() => setRec(id)} data-testid={`rec-${id}`}
                  className={`text-left text-xs font-bold rounded-lg px-3 py-2.5 border transition-colors ${rec === id ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}>{lbl}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600">Estimasi biaya perbaikan (Rp, opsional)</label>
            <Input type="number" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0" className="mt-1.5" data-testid="repair-cost-input" />
          </div>
        </div>
        <Button onClick={submit} disabled={busy} data-testid="submit-report-btn" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl h-11">
          {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : editing ? <Save size={16} className="mr-2" /> : <Send size={16} className="mr-2" />}
          {editing ? "Simpan Perubahan" : "Kirim Laporan ke Pengguna"}
        </Button>
      </div>
    </div>
  );
}
